/**
 * HydraPro – Custom Scripts (custom.js)
 *
 * PURPOSE:
 *   All HydraPro-specific JavaScript lives here, organized under the
 *   CST namespace. This file is loaded deferred after all Dawn scripts,
 *   so Dawn globals (window.routes, window.variantStrings, etc.) are
 *   available when CST code runs.
 *
 * NAMESPACE RULE:
 *   All public symbols must be attached to `window.CST` to avoid
 *   polluting the global scope and conflicting with Dawn or Shopify code.
 *
 * MODULE STRUCTURE:
 *   CST.QuoteCart    – Cart-as-quote-basket logic, quote form handling
 *   CST.SpecTable    – Product spec table value chips
 *   CST.MegaMenu     – Mega menu max-height cap
 *   CST.Turnstile    – Cloudflare Turnstile widgets for form spam protection
 *   CST.FormBackend  – POST helper for the form Worker (contact, enquiry, quote)
 *
 * ADDING CODE:
 *   1. Attach methods to the relevant CST.* module object below.
 *   2. Call the method from CST.init() or a DOMContentLoaded listener.
 *   3. For new features that grow large, create a cst-*.liquid snippet
 *      with an inline <script> that also uses the CST namespace.
 */

'use strict';

/* ============================================================
   NAMESPACE BOOTSTRAP
   Creates the top-level CST object if it doesn't exist yet.
   Safe to call from multiple files.
   ============================================================ */
window.CST = window.CST || {};

/* ============================================================
   CST.QuoteCart — Cart-as-quote-basket logic
   HydraPro repurposes the Shopify cart as a quote request basket.
   Manages three steps: cart → form → success.
   ============================================================ */
CST.QuoteCart = {
  step: 'cart',

  init() {
    const btn = document.querySelector('[data-quote-advance]');
    if (!btn) return;

    document.body.dataset.quoteStep = 'cart';

    // Check URL params
    const params = new URLSearchParams(window.location.search);
    const quoteStep = params.get('quote');

    if (quoteStep === 'form') {
      this.setStep('form');
    }

    btn.addEventListener('click', async () => {
      if (this.step === 'cart') {
        this.setStep('form');
      } else if (this.step === 'form') {
        this.clearSubmitError();

        if (this.validate()) {
          CST.SubmitButton.setSending(btn, true);

          try {
            await handleQuoteSubmission();
            this.setStep('success');
          } catch (err) {
            this.showSubmitError(err.message);
            // Turnstile tokens are single-use — get a fresh one for the retry.
            // Skip while the check is still running, so it isn't restarted.
            const turnstile = document.querySelector('.cst-quote-turnstile');
            if (CST.Turnstile.getToken(turnstile)) CST.Turnstile.reset(turnstile);
            CST.SubmitButton.setSending(btn, false);
          }
        }
      }
    });

    // Clear error state on input
    document
      .querySelectorAll('[data-quote-panel="form"] input, [data-quote-panel="form"] textarea')
      .forEach((field) => {
        field.addEventListener('input', () => {
          field.closest('.cst-quote-field').classList.remove('cst-quote-field--error');
          this.clearSubmitError();
        });
      });

    // Stop change events from the contact form bubbling to CartItems' quantity validator.
    // CartItems does a broad this.addEventListener('change', ...) that incorrectly treats
    // any change event as a quantity input change, calling setCustomValidity on text fields.
    const formPanel = document.querySelector('[data-quote-panel="form"]');
    if (formPanel) {
      formPanel.addEventListener('change', (e) => e.stopPropagation());
    }
  },

  setStep(step) {
    this.step = step;
    document.body.dataset.quoteStep = step;

    const btn = document.querySelector('[data-quote-advance]');
    if (!btn) return;

    if (step === 'form') {
      // From here the button also shows the Turnstile check's progress.
      btn.dataset.cstLabel = btn.dataset.labelForm;
      CST.SubmitButton.bind(btn, {
        container: document.querySelector('.cst-quote-turnstile'),
        canSubmit: () => this.formReady(),
      });
      this.initFormGating(btn);
    } else if (step === 'cart') {
      btn.textContent = btn.dataset.labelCart;
      btn.disabled = false;
    }
  },

  initFormGating(btn) {
    const company = document.getElementById('company_name');
    const email   = document.getElementById('email');
    if (!company || !email) return;

    const sync = () => CST.SubmitButton.update(btn);
    company.addEventListener('input', sync);
    email.addEventListener('input', sync);
  },

  // Required quote fields filled in (company + a valid email).
  formReady() {
    const company = document.getElementById('company_name');
    const email   = document.getElementById('email');
    if (!company || !email) return false;
    return Boolean(company.value.trim() && email.validity.valid && email.value.trim());
  },

  validate() {
    let valid = true;
    document.querySelectorAll('[data-quote-panel="form"] [required]').forEach((field) => {
      const wrapper = field.closest('.cst-quote-field');
      if (!field.value.trim()) {
        wrapper.classList.add('cst-quote-field--error');
        valid = false;
      } else {
        wrapper.classList.remove('cst-quote-field--error');
      }
    });
    return valid;
  },

  showSubmitError(message) {
    const errors = document.getElementById('cart-errors');
    if (errors) errors.textContent = message;
  },

  clearSubmitError() {
    const errors = document.getElementById('cart-errors');
    if (errors) errors.textContent = '';
  },
};

/* ============================================================
   CST.SpecTable — Spec-table value chips
   The product specifications block renders the rich-text
   `product.description`, which contains a two-column table:
   column 1 is a label, column 2 holds one or more values.
   The rich-text editor stores the second-column values as plain
   text (e.g. "Valves, Motors, Cylinders"), so the theme's chip
   styling (.product__specs-description table span) never applies.

   This module wraps each second-column value in its own <span>,
   splitting comma-separated values into separate chips. The
   existing CSS then renders each value as a bordered chip.
   ============================================================ */
CST.SpecTable = {
  init() {
    const tables = document.querySelectorAll('.product__specs-description table');
    if (!tables.length) return;

    tables.forEach((table) => {
      table.querySelectorAll('tr').forEach((row) => {
        const cell = row.querySelectorAll('td')[1]; // second column
        if (!cell) return;

        // Idempotency: skip cells already converted to chips.
        if (cell.dataset.cstChips || cell.querySelector('span')) return;

        const values = cell.textContent
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean);
        if (!values.length) return;

        cell.textContent = '';
        values.forEach((value, index) => {
          if (index > 0) cell.appendChild(document.createTextNode(' '));
          const span = document.createElement('span');
          span.textContent = value;
          cell.appendChild(span);
        });
        cell.dataset.cstChips = 'true';
      });
    });
  },
};

/* ============================================================
   CST.MegaMenu — Cap each mega menu's height to the real remaining
   viewport space below it.
   Dawn's --header-bottom-position-desktop/top:80% combo requires the
   header's height and the content's top offset to stay in lock-step,
   which drifts out of sync easily. Instead, measure each dropdown's
   own actual rendered top on open/resize and set max-height directly
   from that, so the cap always matches reality regardless of header
   markup or positioning changes.
   ============================================================ */
CST.MegaMenu = {
  init() {
    this.menus = document.querySelectorAll('header-menu details.mega-menu');
    if (!this.menus.length) return;

    this.menus.forEach((details) => {
      details.addEventListener('toggle', () => this.scheduleUpdate(details));
      this.watchContent(details);
    });

    window.addEventListener('resize', () => {
      this.menus.forEach((details) => this.updateMaxHeight(details));
    });
  },

  // Switching between already-open 2nd-level items (cst-header-mega-menu.liquid's
  // showSubMenu) swaps the 3rd-level panel's innerHTML without firing a `toggle`
  // event, since the <details> element was already open. Watch for that DOM swap
  // directly so the height cap keeps tracking whichever 2nd-level item is
  // currently hovered, not just whichever one opened the menu.
  watchContent(details) {
    const content = details.querySelector('.mega-menu__content');
    if (!content) return;
    const observer = new MutationObserver(() => this.scheduleUpdate(details));
    observer.observe(content, { childList: true, subtree: true });
  },

  // Re-check a few times after open: brand/collection images loading in can
  // still shift the content's top offset slightly after the initial paint.
  scheduleUpdate(details) {
    this.updateMaxHeight(details);
    requestAnimationFrame(() => this.updateMaxHeight(details));
    setTimeout(() => this.updateMaxHeight(details), 150);
    setTimeout(() => this.updateMaxHeight(details), 400);
  },

  updateMaxHeight(details) {
    if (!details.hasAttribute('open')) return;
    const content = details.querySelector('.mega-menu__content');
    if (!content) return;

    // Clearing maxHeight below lets content (and its flex-stretched left-panel
    // child, .mega-menu__list--condensed) briefly grow tall enough to lose its
    // own overflow, which clamps scrollTop to 0 — reapplying a smaller
    // maxHeight afterwards doesn't restore it, so every hover-triggered
    // recalc was snapping a scrolled left panel back to the top. Save/restore
    // around the swap so the user's scroll position survives it.
    const scrollTargets = [content, ...content.querySelectorAll('.mega-menu__list--condensed')];
    const scrollPositions = scrollTargets.map((el) => el.scrollTop);

    // Force border-box so scrollHeight/max-height both include this
    // element's own padding, regardless of any global box-sizing reset.
    content.style.boxSizing = 'border-box';

    // scheduleUpdate() re-measures this multiple times per open. Clear any
    // cap left by a previous call before measuring, so scrollHeight always
    // reflects this element's true unconstrained content size instead of a
    // stale, already-clamped value (which would shrink further every call).
    content.style.maxHeight = 'none';

    // :root has `zoom: 0.8` (see cst-base.css), which scales rendered/
    // physical measurements (getBoundingClientRect, innerHeight) relative
    // to this element's own local layout units (scrollHeight, and any
    // length we assign to max-height). Convert physical space into local
    // units before using it as a max-height budget, or the cap ends up
    // ~20% smaller than the real available space.
    const zoom = parseFloat(getComputedStyle(document.documentElement).zoom) || 1;

    // offsetHeight (not scrollHeight) because box-sizing is border-box:
    // max-height covers content + padding + border, but scrollHeight
    // excludes the border, which under-allocated by the border width and
    // caused a few px of unnecessary scroll on every menu. max-height is
    // set to 'none' above, so this still reads the true natural size.
    const contentHeight = content.offsetHeight;
    const localTop = content.getBoundingClientRect().top / zoom;
    const localViewportHeight = window.innerHeight / zoom;
    const localAvailable = Math.max(localViewportHeight - localTop - 2, 0);

    // Fit the box to its content unless that would run past the bottom of
    // the viewport; only then clamp it down so it stays on-screen with an
    // internal scrollbar.
    content.style.maxHeight = `${Math.min(contentHeight, localAvailable)}px`;

    scrollTargets.forEach((el, i) => {
      el.scrollTop = scrollPositions[i];
    });
  },
};

/* ============================================================
   CST.Turnstile — Cloudflare Turnstile widgets
   Forms render a [data-cst-turnstile] container via
   snippets/cst-turnstile.liquid. The Turnstile script is loaded
   once, on demand, and each widget is rendered the first time its
   container becomes visible — rendering inside a display:none
   parent (closed modal, hidden quote form panel) can fail.
   Two triggers, whichever comes first: the container scrolling
   into view, or the visitor focusing any field of its form.
   Tokens are single-use: call reset() after every Worker response.

   appearance: 'interaction-only' — the widget stays hidden and only
   shows when Cloudflare needs the visitor to click a checkbox. The
   check's progress is shown in the form's submit button instead
   (CST.SubmitButton), driven by the container's data-cst-state:
     verifying   → check running
     verified    → token ready
     interactive → widget visible, waiting for the visitor's click
     error       → check failed (Turnstile retries automatically)
   Every change fires a 'cst:turnstile-state' event on the container.
   ============================================================ */
CST.Turnstile = {
  SCRIPT_URL: 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
  scriptPromise: null,

  init() {
    const containers = document.querySelectorAll('[data-cst-turnstile]');
    if (!containers.length) return;

    const renderSafely = (container) =>
      this.render(container).catch((err) => console.error('[CST] Turnstile:', err.message));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          renderSafely(entry.target);
        });
      },
      { rootMargin: '200px' }
    );

    containers.forEach((container) => {
      observer.observe(container);

      // Focusing a field means the form is visible — safe to render.
      const form = container.closest('form, [data-quote-panel]');
      if (form) form.addEventListener('focusin', () => renderSafely(container), { once: true });
    });
  },

  load() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!this.scriptPromise) {
      this.scriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = this.SCRIPT_URL;
        script.async = true;
        script.onload = () => resolve(window.turnstile);
        script.onerror = () => {
          this.scriptPromise = null;
          reject(new Error('Turnstile script failed to load'));
        };
        document.head.appendChild(script);
      });
    }
    return this.scriptPromise;
  },

  async render(container) {
    if (!container || container.dataset.widgetId) return;
    const turnstile = await this.load();
    if (container.dataset.widgetId) return; // rendered while the script loaded

    this.setState(container, 'verifying');
    container.dataset.widgetId = turnstile.render(container, {
      sitekey: container.dataset.sitekey,
      action: container.dataset.action,
      size: 'flexible',
      appearance: 'interaction-only',
      'refresh-expired': 'auto',
      language: document.documentElement.lang || 'auto',
      callback: () => this.setState(container, 'verified'),
      'before-interactive-callback': () => this.setState(container, 'interactive'),
      'after-interactive-callback': () => this.setState(container, 'verifying'),
      'expired-callback': () => this.setState(container, 'verifying'),
      'error-callback': () => this.setState(container, 'error'),
    });
  },

  setState(container, state) {
    if (container.dataset.cstState === state) return;
    container.dataset.cstState = state;
    container.dispatchEvent(new CustomEvent('cst:turnstile-state', { detail: { state } }));
  },

  getState(container) {
    return container?.dataset.cstState || 'idle';
  },

  // Returns '' when the widget isn't rendered or the check hasn't finished.
  getToken(container) {
    const id = container?.dataset.widgetId;
    if (!id || !window.turnstile) return '';
    return window.turnstile.getResponse(id) || '';
  },

  reset(container) {
    const id = container?.dataset.widgetId;
    if (!id || !window.turnstile) return;
    this.setState(container, 'verifying');
    window.turnstile.reset(id);
  },
};

/* ============================================================
   CST.FormBackend — POST helper for the form Worker
   (hydrapro-quote-request). Resolves with the parsed JSON on
   success; otherwise throws an Error carrying the Worker's `code`
   ('verification_failed', 'rate_limited', or 'network' when the
   Worker can't be reached) so callers can show a translated message.
   ============================================================ */
CST.FormBackend = {
  async post(url, payload) {
    let res;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch {
      throw Object.assign(new Error('Network error'), { code: 'network' });
    }

    let data = {};
    try {
      data = await res.json();
    } catch {
      // Non-JSON response — handled as a failure below.
    }

    if (!res.ok || !data.success) {
      throw Object.assign(new Error(data.error || `Request failed (${res.status})`), {
        code: data.code || 'failed',
        status: res.status,
      });
    }
    return data;
  },
};

/* ============================================================
   CST.SubmitButton — Turnstile + sending state in a submit button
   Shows the form's verification progress inside its submit button,
   so the (hidden) Turnstile widget needs no UI of its own:
     verifying   → Dawn spinner + data-label-verifying, disabled
     verified    → ✓ + label
     interactive → label, disabled until the visitor completes the
                   checkbox Turnstile shows above the button
     sending     → Dawn spinner + data-label-sending, disabled
   Otherwise the button is enabled when canSubmit() returns true.
   The label is data-cst-label (defaults to the button's text).
   ============================================================ */
CST.SubmitButton = {
  // Dawn's loading-spinner.svg — animated by the .spinner/.path styles in cst-base.css
  SPINNER:
    '<span class="cst-submit__icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg></span>',
  CHECK:
    '<span class="cst-submit__icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path d="M4 10.5l4 4 8-9" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>',

  configs: new WeakMap(),

  bind(button, { container = null, canSubmit = () => true } = {}) {
    if (!button) return;
    if (!button.dataset.cstLabel) button.dataset.cstLabel = button.textContent.trim();
    const alreadyBound = this.configs.has(button);
    this.configs.set(button, { container, canSubmit, sending: false });
    button.classList.add('cst-submit');
    if (container && !alreadyBound) {
      container.addEventListener('cst:turnstile-state', () => this.update(button));
    }
    this.update(button);
  },

  setSending(button, sending) {
    const config = this.configs.get(button);
    if (!config) return;
    config.sending = sending;
    this.update(button);
  },

  update(button) {
    const config = this.configs.get(button);
    if (!config) return;
    const state = CST.Turnstile.getState(config.container);

    let icon = '';
    let text = button.dataset.cstLabel;
    let disabled = !config.canSubmit();
    let busy = false;

    if (config.sending) {
      [icon, text, disabled, busy] = [this.SPINNER, button.dataset.labelSending || text, true, true];
    } else if (state === 'verifying') {
      [icon, text, disabled, busy] = [this.SPINNER, button.dataset.labelVerifying || text, true, true];
    } else if (state === 'interactive') {
      disabled = true;
    } else if (state === 'verified') {
      icon = this.CHECK;
    }

    const content = document.createElement('span');
    content.className = 'cst-submit__content';
    content.innerHTML = icon; // static markup only
    content.append(document.createTextNode(text));
    button.replaceChildren(content);
    button.disabled = disabled;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  },
};

/* ============================================================
   CST.init — Global initialization
   Called once on DOMContentLoaded. Boots all CST modules.
   ============================================================ */
CST.init = function () {
  CST.QuoteCart.init();
  CST.SpecTable.init();
  CST.MegaMenu.init();
  CST.Turnstile.init();
};

document.addEventListener('DOMContentLoaded', CST.init);
